import {
  DatasetObjectQueryReuseHashSource,
  DatasetTableType,
  ProcessingPipelineJobFragment,
  ProcessingPipelineJobStatus,
} from '@warebee/frontend/data-access-api-graphql';
import {
  FilterPreset,
  PolicyFilterBase,
  toLocaleDateTimeDBString,
} from '@warebee/shared/common';
import {
  ActualityBaseQueryBuilderParams,
  ActualityHqDataColumn,
  ActualityQueryBuilderParams,
} from '@warebee/shared/dataset-queries';
import { TFunction } from 'i18next';
import _ from 'lodash';
import * as Icon from '../../components/icons';
import { ActualityAggregatedHqExtendedDataRow } from './actuality.kpi.types';
import { ActualityAutoAnalyzeStatus } from './actuality.types';

export function getActualityAnalyzeJobStatus(
  pipelineJob: ProcessingPipelineJobFragment,
): ActualityAutoAnalyzeStatus {
  switch (pipelineJob.status) {
    case ProcessingPipelineJobStatus.CREATED:
    case ProcessingPipelineJobStatus.QUEUED:
    case ProcessingPipelineJobStatus.STARTING:
    case ProcessingPipelineJobStatus.IN_PROGRESS:
      return 'progress';
    case ProcessingPipelineJobStatus.COMPLETED:
      return 'done';
    case ProcessingPipelineJobStatus.FAILED:
    case ProcessingPipelineJobStatus.TERMINATED:
    case ProcessingPipelineJobStatus.SKIPPED:
      return 'error';
  }
}

export function getActualityAnalyzeJobStatusDescriptors(t: TFunction<'feed'>) {
  const statusMap: Record<
    ActualityAutoAnalyzeStatus,
    {
      label: string;
      state?: string;
      icon?: React.FC<React.SVGProps<SVGSVGElement>>;
    }
  > = {
    none: {
      label: t('Manual', { ns: 'feed' }),
      state: 'draft',
      icon: Icon.Hand,
    },
    progress: {
      label: t('Analysing...', { ns: 'feed' }),
      state: 'analyzing',
      icon: Icon.CircleOk,
    },
    done: {
      label: t('Analysed', { ns: 'feed' }),
      state: 'analyzed',
      icon: Icon.CircleOk,
    },
    error: {
      label: t('Error', { ns: 'feed' }),
      state: 'error',
      icon: Icon.CircleX,
    },
  };

  return statusMap;
}

export function getHqPreset(
  filters: Partial<ActualityAggregatedHqExtendedDataRow>,
): FilterPreset<ActualityHqDataColumn> {
  const preset: FilterPreset<ActualityHqDataColumn> = {
    id: '',
    title: '',
    filterUnion: {
      anyOf: [
        {
          allOf: _.map(filters, (value, field: ActualityHqDataColumn) => {
            const filter: PolicyFilterBase<ActualityHqDataColumn> = {
              type: field,
            };

            if (_.isNil(value)) {
              filter.isNull = true;
            } else {
              filter.valueIn = [
                {
                  title: _.isDate(value)
                    ? toLocaleDateTimeDBString(value)
                    : value,
                },
              ];
            }
            return filter;
          }),
        },
      ],
    },
  };
  return preset;
}

export function getActualityBaseReuseHashSources(
  queryParams: ActualityBaseQueryBuilderParams,
): DatasetObjectQueryReuseHashSource[] {
  const hs: DatasetObjectQueryReuseHashSource[] = [];

  if (queryParams?.documentContext?.documentId) {
    hs.push(
      {
        tableType: DatasetTableType.ACTIVITY_FEED,
        id: queryParams?.documentContext?.documentId,
      },
      {
        tableType: DatasetTableType.ACTUALITY_EXTENDED_EVENT,
        id: queryParams?.documentContext?.documentId,
      },
      {
        tableType: DatasetTableType.ANALYZED_JOB_EVENT,
        id: queryParams?.documentContext?.documentId,
      },
    );
  }

  if (queryParams?.activity?.datasetId) {
    hs.push({
      tableType: DatasetTableType.ACTUALITY_EMPLOYEE_SHIFT,
      id: queryParams.activity.datasetId,
    });
  }

  return hs;
}

export function getActualityReuseHashSources(
  queryParams: ActualityQueryBuilderParams,
): DatasetObjectQueryReuseHashSource[] {
  const hs = getActualityBaseReuseHashSources(queryParams);
  if (queryParams?.layout?.datasetId) {
    hs.push({
      tableType: DatasetTableType.LAYOUT_LOCATION,
      id: queryParams.layout.datasetId,
    });
  }
  if (queryParams?.itemSet?.datasetId) {
    hs.push({
      tableType: DatasetTableType.ITEM_SET,
      id: queryParams.itemSet.datasetId,
    });
  }
  if (queryParams?.assignment?.datasetId) {
    hs.push({
      tableType: DatasetTableType.ASSIGNMENT,
      id: queryParams.assignment.datasetId,
    });
  }

  return hs;
}
